import React from "react";
import dayjs from "dayjs";

import { Container, Grid, FormControl, FormGroup, FormHelperText, Typography } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";

import CustomInput from "../../../../helper-components/input/CustomInput";
import CustomStepper from "../../../../helper-components/form/CustomStepper";
import ConfirmationDialog from "../../../../helper-components/input/ConfirmationDialog";
import TransparentFragment from "../../../../helper-components/misc/TransparentFragment";

import { useDialog } from "../../../../helper-functions/hooks/useDialog";
import { addFormToObj, setInsertObjectState, setValue } from "../../../../helper-functions/shared/miscUtils";
import { validateRow } from "../../../../helper-functions/validation/objectValidation";

import { PATIENT_VALIDATION, IS_PATIENT_VALID } from "../../../../helper-functions/validation/patientValidation";
import { PATIENT_REQUEST_OBJECT } from "../../../../config/constants/Constants";
import { getLanguage } from "../../../../config/language/language";
import { formatDate } from "../../../../helper-functions/shared/miscUtils";

/**
 * A stepper component to add a new patient.
 * Includes helper descriptions and validation.
 */

export default function PatientAddStepper(props) {
    const { enqueueDialog, setOpen, ...options } = useDialog();
    const { callback, row: existingRow = {} } = props;

    const object = PATIENT_REQUEST_OBJECT;
    const validation = PATIENT_VALIDATION;

    const [row, setRow] = React.useState(addFormToObj(JSON.parse(JSON.stringify(object))));

    React.useEffect(() => {
        const newRow = {
            ...row,
            ...existingRow,

            birth_date: existingRow.birth_date ? dayjs(existingRow.birth_date) : null,

            form: {
                ...row.form,
                ...existingRow.form,
                saved: false
            }
        };
        setInsertObjectState(setRow, newRow);
    }, []);

    const onChange = (obj, key, value) => {
        const newRow = JSON.parse(JSON.stringify(row));
        
        // Restore dayjs objects lost during JSON serialization (stringify converts to UTC string)
        if (row.birth_date) newRow.birth_date = row.birth_date;

        setValue(newRow, key, value);
        validateRow(obj, validation, newRow);
        setInsertObjectState(setRow, newRow);
    };

    const handleClick = (confirm) => {
        const options = { row };
        callback(confirm, options);
    };

    const step1 = () => (
        <Container style={{ margin: 0, padding: 0 }}>
            <Grid container spacing={1}>
                <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                        {getLanguage().PATIENT_DETAILS_HINT}
                    </Typography>
                </Grid>

                <Grid item xs={12}>
                    <CustomInput name="patient_identifier" row={row} onChange={onChange} label="Patient ID" />
                </Grid>

                <Grid item xs={6}>
                    <CustomInput name="first_name" row={row} onChange={onChange} label="First Name" />
                </Grid>

                <Grid item xs={6}>
                    <CustomInput name="last_name" row={row} onChange={onChange} label="Last Name" />
                </Grid>

                <Grid item xs={12}>
                    <FormControl variant="standard" sx={{ width: "100%" }}>
                        <FormGroup sx={{ alignContent: "center" }}>
                            <DatePicker
                                label="Birthday"
                                format="MM/DD/YYYY"
                                value={row.birth_date}
                                onChange={(value) => onChange(row, "birth_date", value)}
                                sx={{ width: "100%" }}
                            />
                        </FormGroup>
                        <FormHelperText sx={{ alignSelf: "left", marginLeft: 2 }}>{row?.form?.birth_date?.helperText}</FormHelperText>
                    </FormControl>
                </Grid>

                <Grid item xs={12}>
                    <CustomInput name="notes" row={row} onChange={onChange} label="Notes" multiline minRows={2} maxRows={5} />
                </Grid>
            </Grid>
        </Container>
    );

    const steps = [
        {
            label: "Patient Details",
            optional: false,
            component: step1(),
            nextDisabled: !IS_PATIENT_VALID(row)
        }
    ];

    const final = {
        component: (
            <Container style={{ margin: 0, padding: 0 }}>
                <Grid container spacing={1}>
                    <Grid item xs={12}>
                        <TransparentFragment
                            value={`Review and confirm patient details before saving:
                            * Patient ID: ${row.patient_identifier?.trim() || "N/A"}
                            * First name: ${row.first_name?.trim() || "N/A"}
                            * Last name: ${row.last_name?.trim() || "N/A"}
                            * Birthday: ${row.birth_date ? formatDate(row.birth_date, { onlyDate: true }) : "N/A"}
                            `}
                        />
                    </Grid>
                </Grid>
            </Container>
        )
    };

    return (
        <>
            <ConfirmationDialog {...props} options={{ enqueueDialog, setOpen, ...options }} />
            <CustomStepper steps={steps} final={final} callback={(confirm) => handleClick(confirm)} />
        </>
    );
}
